import React from 'react';
import { calculateChartScale, ScaleConfig } from '@/utils/chartScaling';
import { ChartTooltipCard } from '@/components/shared/ChartTooltipCard';

interface BarData {
  label: string;
  value: number;
}

interface BarChartProps {
  data: BarData[];
  className?: string;
  unit?: 'units' | 'percent' | 'persons';
  scaleConfig?: ScaleConfig;
  yAxisLabel?: string;
  xAxisLabel?: string;
  barSpacing?: 'normal' | 'compact';
}

export const snapshot_BarChart: React.FC<BarChartProps> = ({ 
  data, 
  className = "", 
  unit = "units",
  scaleConfig = {
    padding: 0.1,
    tickCount: 5,
    includeZero: true
  },
  yAxisLabel,
  xAxisLabel,
  barSpacing = 'normal'
}) => {
  // Chart dimensions
  const CHART_HEIGHT = 300;
  const MIN_BAR_HEIGHT = 1;
  const MIN_BAR_WIDTH = 20;

  /* ------ Responsive axis spacing (configurable) ------ */
  const AXIS_WIDTH_DESKTOP = 80;
  const AXIS_WIDTH_MOBILE  = 60;
  const Y_AXIS_LABEL_WIDTH_DESKTOP = 18;
  const Y_AXIS_LABEL_WIDTH_MOBILE  = 14;
  /* --------------------------------------------------- */

  const BREAKPOINT_MD = 768;
  const MIN_GAP = 8; // minimum desired gap between bars in px on mobile

  /* Track viewport width so we can decide mobile vs desktop */
  const [viewportWidth, setViewportWidth] = React.useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  /* ---------------- Tooltip boundary handling ---------------- */
  const [hoveredBarIndex, setHoveredBarIndex] = React.useState<number | null>(null);
  const [hoveredIsNegative, setHoveredIsNegative] = React.useState<boolean>(false);
  // Stores computed adjustments and placement for the visible tooltip
  const [tooltipShift, setTooltipShift] = React.useState<{ offsetX: number; offsetY: number; above: boolean }>({ offsetX: 0, offsetY: 0, above: true });
  // Ref to the *visible* tooltip element so we can measure its size/position
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  /* ---------------------------------------------------------------- */

  React.useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    // Re-calculate tooltip shifts whenever the hovered bar, viewport, or placement context changes.
    if (hoveredBarIndex === null) return;
    if (!tooltipRef.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const PADDING = 8; // desired gap from container edges
    let offsetX = 0;
    let offsetY = 0;

    // Horizontal clamping
    if (tooltipRect.left < containerRect.left + PADDING) {
      offsetX = containerRect.left + PADDING - tooltipRect.left;
    } else if (tooltipRect.right > containerRect.right - PADDING) {
      offsetX = containerRect.right - PADDING - tooltipRect.right;
    }

    // Baseline placement: above for positive bars, below for negative bars
    const placeAbove = true; //!hoveredIsNegative

    // Vertical clamping independent of placement
    if (tooltipRect.top < containerRect.top + PADDING) {
      // Shift down
      offsetY = containerRect.top + PADDING - tooltipRect.top;
    } else if (tooltipRect.bottom > containerRect.bottom - PADDING) {
      // Shift up
      offsetY = containerRect.bottom - PADDING - tooltipRect.bottom;
    }

    setTooltipShift(prev => {
      if (prev.offsetX === offsetX && prev.offsetY === offsetY && prev.above === placeAbove) return prev;
      return { offsetX, offsetY, above: placeAbove };
    });
  }, [hoveredBarIndex, hoveredIsNegative, viewportWidth]);

  const isMobileScreen = viewportWidth < BREAKPOINT_MD;
  const AXIS_WIDTH = isMobileScreen ? AXIS_WIDTH_MOBILE : AXIS_WIDTH_DESKTOP;
  const Y_AXIS_LABEL_WIDTH = isMobileScreen ? Y_AXIS_LABEL_WIDTH_MOBILE : Y_AXIS_LABEL_WIDTH_DESKTOP;

  // Format values for display
  // Unified value formatter used for both ticks and tooltips. Mirrors the logic in OverTimeChartCard so numbers look identical across visualizations.
  const formatValue = (value: number, isAxisLabel: boolean = false): string => {
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

    if (isAxisLabel) {
      // Axis labels should remain unit-agnostic (we already paint the unit in the y-axis label component).
      return formatted;
    }

    // Decide what (if any) unit suffix to append, replicating OverTimeChartCard behaviour.
    // • percent  → "%"
    // • units / persons → no explicit suffix (consistent with line-chart where metricUnits is empty string)
    // • any other custom unit string → append as-is
    const suffix =
      unit === 'percent'
        ? '%'
        : unit === 'units' || unit === 'persons'
        ? ''
        : unit;

    return `${formatted}${suffix}`;
  };

  // Calculate scale using our utility
  const values = data.map(d => d.value);
  const scale = calculateChartScale(values, {
    ...scaleConfig,
    dataType: unit === 'percent' ? 'percentage' : 'unit',
    customFormatter: (value) => formatValue(value, true),
    includeZero: true,
    // Enforce min zero for both percentage and unit data since negative values don't make sense
    enforceMinZero: scaleConfig.enforceMinZero || unit === 'units'
  });

  // Calculate the zero position in the chart space (from top)
  const zeroY = ((scale.max) / (scale.max - scale.min)) * CHART_HEIGHT;

  // Calculate bar dimensions
  const calculateBarDimensions = (value: number): { height: number; y: number } => {
    if (scale.max === scale.min) return { height: MIN_BAR_HEIGHT, y: zeroY };

    const range = scale.max - scale.min;
    const valueHeight = Math.abs(value) / range * CHART_HEIGHT;
    const height = Math.max(MIN_BAR_HEIGHT, valueHeight);

    if (value >= 0) {
      // Positive values: grow up from zero line
      return {
        height,
        y: zeroY - height // Position above zero line
      };
    } else {
      // Negative values: grow down from zero line
      return {
        height,
        y: zeroY // Position below zero line
      };
    }
  };

  // Generate y-axis labels and positions
  const yAxisPositions = scale.ticks.map((tick, index) => {
    const normalizedValue = (scale.max - tick.value) / (scale.max - scale.min);
    return normalizedValue * CHART_HEIGHT;
  });

  // Calculate dynamic bar width based on container width
  const useBarSizing = () => {
    const [containerWidth, setContainerWidth] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth);
        }
      };

      updateWidth();

      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, []);

    /*
      NEW BAR + GAP MATH
      -------------------------------------------------
      1. Pick a preferred fixed bar width based on breakpoint (80 / 40)
      2. Derive how much horizontal space remains → compute gap size
      3. If the preferred width would cause overflow, shrink bar width (not below 20px)
    */
    const calculateSizing = () => {
      const numBars = data.length;
      if (!containerWidth || numBars === 0) {
        return { barWidth: 0, gap: 0 };
      }

      const preferredBarWidth = containerWidth < BREAKPOINT_MD ? 40 : 80;
      const numGaps = numBars === 5 ? numBars : Math.max(0, numBars - 1);

      // First attempt: fixed bar width, min gap satisfied
      let gap = numGaps > 0 ? MIN_GAP : 0;
      let barWidth = preferredBarWidth;

      let requiredSpace = barWidth * numBars + gap * numGaps;

      // If we overflow, shrink bars (not below MIN_BAR_WIDTH) but keep min gap
      if (requiredSpace > containerWidth) {
        barWidth = Math.max(MIN_BAR_WIDTH, (containerWidth - gap * numGaps) / numBars);
        requiredSpace = barWidth * numBars + gap * numGaps;
      }

      // If we STILL overflow (because gaps consume too much), let gap shrink
      if (requiredSpace > containerWidth) {
        gap = numGaps > 0 ? Math.max(0, (containerWidth - barWidth * numBars) / numGaps) : 0;
        requiredSpace = barWidth * numBars + gap * numGaps;
      }

      // Finally, if we have leftover space, distribute it evenly to gaps
      const remaining = containerWidth - requiredSpace;
      if (remaining > 0 && numGaps > 0) {
        gap += remaining / numGaps;
      }

      return { barWidth, gap };
    };

    const { barWidth, gap } = calculateSizing();

    // Edge padding (only for 5-bar charts so the middle bar is centred)
    const edgePadding = data.length === 5 ? `${gap / 2}px` : '0px';

    return {
      ref: containerRef,
      barWidth,
      gap,
      edgePadding,
    };
  };

  const { ref: containerRef, barWidth, gap, edgePadding } = useBarSizing();

  return (
    <div className={`bg-white relative w-full text-base text-[#212121] font-semibold whitespace-nowrap uppercase tracking-[0.32px] leading-[1.3] mt-10 pb-6 max-md:max-w-full font-geist-mono ${className}`}>
      {/* Y-axis label (hidden on mobile) */}
      {yAxisLabel && !isMobileScreen && (
        <div 
          className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-[#666666] font-normal leading-tight align-middle chart-text-shift snapshot-axis-label"
          style={{
            width: Y_AXIS_LABEL_WIDTH,
            transform: 'rotate(-90deg) translateX(-50%)',
            transformOrigin: '0 50%',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            lineHeight: '1.2',
            verticalAlign: 'middle'
          }}
        >
          {yAxisLabel}
        </div>
      )}
      
      {/* Y-axis labels */}
      <div 
        className="absolute top-0 h-full flex flex-col justify-between pb-[30px]"
        style={{ 
          left: yAxisLabel && !isMobileScreen ? `${Y_AXIS_LABEL_WIDTH}px` : '0',
          width: `${AXIS_WIDTH - 10}px`
        }}
      >
        {scale.ticks.map((tick, index) => (
          <div 
            key={index} 
            className="absolute text-right pr-4 text-xs text-[#666666] font-normal leading-tight align-middle chart-text-shift snapshot-axis-label"
            style={{
              top: `${yAxisPositions[index]}px`,
              right: 0,
              transform: 'translateY(-50%)',
              width: '100%',
              lineHeight: '1.2',
              verticalAlign: 'middle'
            }}
          >
            {tick.label}
          </div>
        ))}
      </div>
      
      {/* Chart area */}
      <div 
        ref={containerRef}
        className={`h-[300px] relative mb-8`}
        style={{ 
          marginLeft: yAxisLabel && !isMobileScreen 
            ? `${AXIS_WIDTH + Y_AXIS_LABEL_WIDTH}px` 
            : `${AXIS_WIDTH}px`
        }}
      >
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none">
          {scale.ticks.map((tick, index) => (
            <div
              key={`grid-line-${index}`}
              className={`absolute w-full border-b ${tick.value === 0 ? 'border-black/20 border-solid' : 'border-dotted border-black/10'}`}
              style={{ 
                top: `${yAxisPositions[index]}px`
              }}
            />
          ))}
        </div>

        {/* Bars container */}
        <div 
          className="absolute inset-0 flex"
          style={{
            gap: `${gap}px`,
            paddingLeft: edgePadding,
            paddingRight: edgePadding
          }}
        >
          {/* Bars */}
          {data.map((bar, index) => {
            const { height, y } = calculateBarDimensions(bar.value);
            const isNegative = bar.value < 0;

            return (
              <div
                key={index}
                className="flex flex-col items-center relative hover:z-10"
                style={{ 
                  width: `${barWidth}px` 
                }}
                onMouseEnter={() => {
                  setHoveredBarIndex(index);
                  setHoveredIsNegative(isNegative);
                }}
                onMouseLeave={() => {
                  setHoveredBarIndex(null);
                  setHoveredIsNegative(false);
                }}
              >
                <div 
                  className={`w-full relative transition-all duration-200 ${isNegative ? 'rounded-[0px_0px_10px_10px]' : 'rounded-[10px_10px_0px_0px]'}`}
                  style={{ 
                    height: `${height}px`,
                    transform: `translateY(${y}px)`,
                    background: 'linear-gradient(180deg, rgba(202, 0, 121, 0.50) 0%, rgba(243, 186, 211, 0.50) 100%)'
                  }}
                >
                  {/* Tooltip */}
                  {(
                    <div
                      ref={hoveredBarIndex === index ? tooltipRef : null}
                      className="absolute z-10 transition-opacity duration-150"
                      style={{
                        opacity: hoveredBarIndex === index ? 1 : 0,
                        left: '50%',
                        transform: `translateX(-50%) translateY(${tooltipShift.offsetY}px)`,
                        marginLeft: hoveredBarIndex === index ? tooltipShift.offsetX : 0,
                        ...(tooltipShift.above
                          ? { bottom: '100%', marginBottom: '4px' }
                          : { top: '100%', marginTop: '4px' }),
                      }}
                    >
                      <ChartTooltipCard
                        year={bar.label}
                        metricLabel={yAxisLabel ?? (unit === 'percent' ? 'Percent' : unit)}
                        value={formatValue(bar.value)}
                      />
                    </div>
                  )}
                </div>
                <div 
                  className="text-center text-xs text-[#666666] font-normal absolute -rotate-45 sm:rotate-0 origin-top sm:origin-center left-[calc(50%-4px)] -translate-x-1/2 sm:left-auto sm:translate-x-0 translate-y-2 sm:translate-y-0 snapshot-axis-label"
                  style={{
                    bottom: '-24px',
                    width: '100%'
                  }}
                >
                  {bar.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* X-axis label */}
      {xAxisLabel && (
        <div 
          className="text-center text-xs text-[#666666] font-normal mt-2 snapshot-axis-label"
          style={{
            marginLeft: yAxisLabel && !isMobileScreen 
              ? `${AXIS_WIDTH + Y_AXIS_LABEL_WIDTH}px` 
              : `${AXIS_WIDTH}px`
          }}
        >
          {xAxisLabel}
        </div>
      )}
    </div>
  );
};